(ns ui.editors.simple-filters.commit
  "When the value that a user types into a filter becomes a filter, and what the
   filter is. The rules are pure, so that they can be tested. The view calls them
   from its input handlers.

   A value becomes a filter when the user presses Enter, when the user leaves the
   input, when typing stops for `debounce-ms`, and when the user changes the
   operator or the currency while the filter has a value. Before, only leaving
   the input applied the value. A user who typed a number and pressed Enter saw
   no change, and the panel showed a condition that the list did not use."
  (:require
   [clojure.string :as string]
   [ui.view.opensearch-value.currency :as value-currency]))

(def debounce-ms
  "How long after the last key press the typed value is applied. A shorter time
   sends a query for each digit. A longer time looks like no reaction."
  500)

(def scalar-numeric-operators
  "The numeric operators that take one value. The between operator takes two."
  #{:operand/equals
    :operand/smaller-than
    :operand/greater-than})

(defn- blank-value?
  [value]
  (string/blank? (str value)))

(def ^:private grouped-integer-patterns
  "For each thousand separator, an integer with groups of 3 digits, as in
   1,234,567. The first group has 1 to 3 digits, and does not start with 0."
  {"," #"[1-9]\d{0,2}(,\d{3})+"
   "." #"[1-9]\d{0,2}(\.\d{3})+"})

(defn- grouped-integer?
  [text separator]
  (some? (re-matches (get grouped-integer-patterns separator) text)))

(defn- normalize-unsigned-number-text
  "`text` with no sign, with a point as the decimal separator and with no
   thousand separators, or nil when the separators are not correct. See
   `parse-number-text`."
  [text decimal-separator]
  (let [comma-count (count (re-seq #"," text))
        point-count (count (re-seq #"\." text))]
    (cond
      (re-matches #"(\d+(\.\d*)?|\.\d+)[eE][+-]?\d+" text)
      (let [n (js/Number text)]
        (when (js/isFinite n)
          (str n)))

      (and (pos? comma-count) (pos? point-count))
      (let [decimal-index (max (string/last-index-of text ",")
                               (string/last-index-of text "."))
            separator (if (= "," (subs text decimal-index (inc decimal-index))) "." ",")
            integer-part (subs text 0 decimal-index)]
        (when (grouped-integer? integer-part separator)
          (str (string/replace integer-part separator "")
               "."
               (subs text (inc decimal-index)))))

      (or (< 1 comma-count) (< 1 point-count))
      (let [separator (if (< 1 comma-count) "," ".")]
        (when (grouped-integer? text separator)
          (string/replace text separator "")))

      (= 1 (+ comma-count point-count))
      (let [separator (if (pos? comma-count) "," ".")]
        (if (and (not= separator decimal-separator)
                 (grouped-integer? text separator))
          (string/replace text separator "")
          (string/replace text separator ".")))

      :else
      text)))

(def ^:private space-grouped-number-pattern
  "A number with a space or an apostrophe between groups of 3 digits, as in
   250 000,42 or 1'234.5. The first group has 1 to 3 digits, and does not start
   with 0. One decimal separator can follow the groups."
  #"[+-]?[1-9]\d{0,2}([ ']\d{3})+([.,]\d*)?")

(defn parse-number-text
  "The number that a user typed into a filter, as a text that `js/Number`
   reads, or nil when the text is not a number. The text is never changed
   silently.

   - A space and an apostrophe are thousand separators. They must be between
     groups of 3 digits, so the text 1 2 is not a number.
   - A text with a comma and a point: the last one is the decimal separator,
     and the other one is a thousand separator, as in 250,000.42 or 250.000,42.
   - A comma or a point that comes two times or more is a thousand separator.
   - A comma or a point that comes one time is the decimal separator. When it
     is not `decimal-separator` (the decimal separator of the browser) and the
     text is a correct group of thousands, it is a thousand separator, as in
     250,000. The text 0.125 is a decimal, because 0 cannot start a group.
   - A thousand separator must be between groups of 3 digits, so the text
     1.2.3 is not a number.
   - A number with an exponent, as in 1e6 or 1e-7, and the sign + are
     permitted. The result can have an exponent, as in 1e+21."
  [text decimal-separator]
  (let [trimmed (-> (str text)
                    string/trim
                    (string/replace #"[\s  ]+" " "))
        space-grouped? (re-find #"[ ']" trimmed)
        [_ sign unsigned] (when (or (not space-grouped?)
                                    (re-matches space-grouped-number-pattern trimmed))
                            (->> (string/replace trimmed #"[ ']" "")
                                 (re-matches #"([+-]?)(.*)")))
        normalized (some->> (some-> unsigned
                                    (normalize-unsigned-number-text decimal-separator))
                            (str (when (= "-" sign) "-")))]
    (when (and normalized
               (re-matches #"-?(\d+(\.\d*)?|\.\d+)(e[+-]?\d+)?" normalized))
      normalized)))

(defn invalid-number-text?
  "True when the user typed a text that is not a number, as `parse-number-text`
   reads it. A blank text is not invalid: it gives no filter."
  [text decimal-separator]
  (and (not (blank-value? text))
       (nil? (parse-number-text text decimal-separator))))

(defn numeric-op-state
  "`op-state` with the typed numbers in `:value`, `:from` and `:to` read by
   `parse-number-text`. A typed text that is not a number becomes blank, so it
   gives no filter."
  [op-state decimal-separator]
  (reduce (fn [state k]
            (if (contains? state k)
              (update state k (fn [text]
                                (or (parse-number-text text decimal-separator) "")))
              state))
          op-state
          [:value :from :to]))

(defn enter-key?
  "True for the key name of the Enter key, as `KeyboardEvent.key` gives it."
  [key-name]
  (= "Enter" key-name))

(defn scalar-selection
  "Return the selection for a filter with one value, for example `greater than
   100000`, or nil when the value is blank. `op-state` is the local state of the
   operator panel: `:op`, `:value` and `:currency`. The selection has a currency
   only for a currency field."
  [{:keys [op value currency]} currency-field?]
  (when-not (blank-value? value)
    {:kind     :value
     :operator op
     :currency (when currency-field? currency)
     :value    value
     :label    (str value)}))

(defn between-selection
  "Return the selection for a filter with two values, for example `between 10
   and 20`, or nil when one of the two values is blank. `op-state` is the local
   state of the operator panel: `:op`, `:from`, `:to` and `:currency`."
  [{:keys [op from to currency]}]
  (when-not (or (blank-value? from)
                (blank-value? to))
    {:kind     :value
     :operator op
     :from     from
     :to       to
     :currency currency
     :value    (str from " .. " to)
     :label    (str from " .. " to)}))

(defn with-currency-field
  "`selection` with `:currency-field-path`, the path of the field that holds the
   currency of the values of a currency field. The filter then also matches the
   chosen currency. A selection with no currency, and a selection of a field that
   is not a currency field, stay as they are. `field-schema` is the field of the
   panel. It has the schema of the field under `:field/schema`, and only the schema
   has the secondary attribute that holds the currency. A row with no currency
   shows `default-currency`, the default currency of the tenant, so a selection of
   that currency also matches a row with no currency (`:default-currency?`)."
  [selection {:keys [field-path field-schema default-currency]}]
  (let [currency-field-path (value-currency/currency-field-path
                             {:field-path   field-path
                              :field-schema (merge field-schema (:field/schema field-schema))})
        currency (:currency selection)]
    (cond-> selection
      (and (some? currency)
           (some? currency-field-path))
      (assoc :currency-field-path currency-field-path
             :default-currency?   (and (some? default-currency)
                                       (= (keyword currency) (keyword default-currency)))))))

(defn operator-switch-selection
  "Return the selection to apply when the user switches to the numeric operator
   `op` and the panel already holds a value, or nil when there is nothing to
   apply yet. A scalar operator needs `:value`. The between operator needs both
   `:from` and `:to`. `op-state` is the local state with the new operator."
  [{:keys [op] :as op-state} currency-field?]
  (cond
    (contains? scalar-numeric-operators op)
    (scalar-selection op-state currency-field?)

    (= :operand/between-inclusive op)
    (between-selection op-state)

    :else
    nil))
